// import type { AgentHash } from '../pubsub/pubsub-types.ts'
// import type { CID } from '@oddjs/odd'
import { FormatRegistry, Static, TSchema, Type } from '@sinclair/typebox'
import { TypeCompiler } from '@sinclair/typebox/compiler'
import { CID } from 'multiformats/cid'
import type { PartialBy, Tagged } from '../types/typescript-utils.ts'

export const Nullable = <T extends TSchema>(schema: T) => Type.Union([schema, Type.Null()])
export const EntityID_LENGTH = 7
// const bagu = 'baguqeerav3h4b46j2pyxikqhtm5si5vhzsyrba2duhrtltfutrlmj42anmvq'
// const k51q = 'k51qzi5uqu5dhe1bxxjxj144bj2a225o1681yobevns26xlxtsfidjgnpwknfd'
const isCID = /^(k51qz|baguq)[0-9a-z]{56,57}$/ // FIXME: k51 is not really a CID, is it?
const isShortHash = /^[0-9A-Fa-f]{7,8}$/g // TODO awkward why are some 7 and some 8 long
// engine level: min 6 (lenient within reason)
// note3 TBD... either fixed for all entity types VS 7 for pub/sub, 8 for tags, 9 for blocks, 10 for relations etc...

FormatRegistry.Set('EntityID', (value) => !!value.match(isShortHash) || !!value.match(isCID))
export const EntityID = Type.String() // HACK how to configure ID format?
/*{  format: 'EntityID'  }*/
export type EntityID = Static<typeof EntityID>

export type AgentHash = Tagged<string, 'AgentHash'>
export type DatomPart = string // TODO refactor
export type CidString = Tagged<string, CID>
export type IpnsString = Tagged<CidString, 'IPNS'>
export type AgentID = EntityID
export type Attribute = string
// JSON-serializable value space for a datom's `vl` field.
export type JsonPrimitive = string | boolean | number | null
export interface JsonObject {
	[key: string]: JsonValue
}
export type JsonArray = JsonValue[]
export type JsonValue = JsonPrimitive | JsonObject | JsonArray
export type ApplogValue = JsonValue // TODO: use Tagged types

export interface Atom {
	en: EntityID
	at: Attribute
	vl: ApplogValue
}

export type Timestamp = string
export interface Applog extends Atom {
	cid: CidString
	pv: CidString | null // ? | CID
	ts: Timestamp
	ag: AgentHash
}
export type ApplogNoCid = Omit<Applog, 'cid'>
export type ApplogOptionalCid = PartialBy<Applog, 'cid'>
export type ApplogForInsert = PartialBy<ApplogNoCid, 'ts' | 'pv'>
export type ApplogForInsertOptionalAgent = PartialBy<ApplogForInsert, 'ag'>

export interface ApplogEnc {
	cid: CidString
	enc: Uint8Array
	iv?: Uint8Array // iv may be needed if we prefer a different strategy for transmitting iv (odd appends a random iv to the payload via keystoreAES.(en|de)cryptBytes)
}
export type ApplogEncNoCid = Omit<ApplogEnc, 'cid'>

export type ApplogArrayMaybeEncrypted = (Applog | ApplogEnc)[]
export type ApplogArrayMaybeEncryptedRO = readonly (Applog | ApplogEnc)[]
export type ApplogArrayNoCIDMaybeEncryptedRO = readonly (ApplogNoCid | ApplogEncNoCid)[]

export type ApplogOfSomeSort = Applog | ApplogEnc | ApplogNoCid | ApplogEncNoCid | ApplogForInsert

export const isEncryptedApplog = (l: ApplogOfSomeSort): l is ApplogEnc => (l as ApplogEnc)?.enc instanceof Uint8Array

export type AtomPattern = Atom | Applog

export interface DatalogStateIdentifier {
	lastTS: Timestamp
}

// New generic type for fields that can be a value, an array of that, or a function
export type ValueOrMatcher<T> = T | readonly T[] | ReadonlySet<T> | ((value: T) => boolean)
// Generic type that applies ValueOrMatcher to each field of T
export type WithMatchers<T extends Record<string, any>> = {
	[K in keyof T & string as `${K}` | `!${K}`]?: ValueOrMatcher<T[K]>
}

export type DatalogQueryPattern = Partial<WithMatchers<Applog>>
export type DatalogQueryPatternArray = DatalogQueryPattern[]
export interface DatalogQuery<SELECT extends string> {
	find: readonly SELECT[] // see: https://github.com/microsoft/TypeScript/issues/20965#issuecomment-868981458
	where: DatalogQueryPatternArray
	onlyLatest?: boolean
}
export type DatalogQueryResultEntry<SELECT extends string> = Record<
	// SELECT,
	StripPrefix<'?', SELECT>,
	DatomPart
>
export type DatalogQueryResultRows<SELECT extends string> = DatalogQueryResultEntry<SELECT>[]
// export type StripTest = StripPrefix<'?', '?A' | '?B'>
// export type DatalogQueryResultEntryTEST = DatalogQueryResultEntry<'?A' | '?B'>
// export type DatalogQueryResultEntryTESTX = MapKeysStripPrefix<'?A' | '?B', '?'>

export interface SearchContext {
	[key: string]: ApplogValue
}
export interface SearchContextWithLog {
	context: SearchContext
	applog?: Applog
}

export type ResultContext = SearchContext | null

/* https://stackoverflow.com/a/72497461 */
type StripPrefix<
	TPrefix extends string,
	T extends string,
> = T extends `${TPrefix}${infer R}` ? R : never

type MapKeysStripPrefix<SELECT extends string, TPrefix extends string> = {
	[K in SELECT as StripPrefix<TPrefix, K>]: DatomPart
}

FormatRegistry.Set('CID', (value) => !!value.match(isCID))
export const CIDTB = Type.String({ pattern: isCID.source })
export type CIDTB = Static<typeof EntityID>

const isURL = /^http([s]?):\/\/.*\..*/
FormatRegistry.Set('URL', (value) => !!value.match(isURL))
export const URL = Type.String({ pattern: isURL.source })
export type URL = Static<typeof URL>

// Recursive JSON value: primitives plus nested arrays/objects (mirrors ApplogValue / JsonValue).
export const JsonValueTB = Type.Recursive(This =>
	Type.Union([
		Type.String(),
		Type.Number(),
		Type.Boolean(),
		Type.Null(),
		Type.Array(This),
		Type.Record(Type.String(), This),
	])
)
export type JsonValueTB = Static<typeof JsonValueTB>

export const AppLogNoCidTB = Type.Object({
	en: EntityID, // EntityID
	at: Type.String(), // Attribute
	vl: JsonValueTB, // ApplogValue (JSON-serializable: primitives, arrays, objects)
	ts: Type.String(), // Timestamp
	ag: Type.String(), // AgentHash
	pv: Nullable(CIDTB), // CidString
})
export type AppLogNoCidTB = Static<typeof AppLogNoCidTB> // type T = {

export const AppLogNoCidTBC = TypeCompiler.Compile(AppLogNoCidTB)
export const getApplogNoCidTypeErrors = (obj: any) => Array.from(AppLogNoCidTBC.Errors(obj))
export const isValidApplogNoCid = AppLogNoCidTBC.Check.bind(AppLogNoCidTBC) // ? Include CID

export const AppLogTB = Type.Composite([
	Type.Object({
		cid: CIDTB,
	}),
	AppLogNoCidTB,
])
export type AppLogTB = Static<typeof AppLogTB> // type T = {

export const AppLogTBC = TypeCompiler.Compile(AppLogTB)
export const getApplogTypeErrors = (obj: any) => Array.from(AppLogTBC.Errors(obj))
export const isValidApplog = AppLogTBC.Check.bind(AppLogTBC) // ? Include CID
// maybe useful for defaulting https://github.com/sinclairzx81/typebox#cast
